var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useState, } from 'react';
import { Button, ButtonText, ButtonUnderline, ButtonsContainer, Tab, TabBar, } from './styled';
const cycleIndexLeft = (index, length) => index > 0 ? index - 1 : length - 1;
const cycleIndexRight = (index, length) => index < length - 1 ? index + 1 : 0;
const scrollToTabId = (id) => {
    var _a;
    return (_a = document
        .getElementById(`tab-${id}`)) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ behavior: 'smooth', inline: 'nearest' });
};
export function Tabs(_a) {
    var { children, activeTabId = children.flat()[0].props.id, description, onChange, className } = _a, props = __rest(_a, ["children", "activeTabId", "description", "onChange", "className"]);
    const [currentTabId, setCurrentTab] = useState(activeTabId);
    const [currentFocusId, setCurrentFocus] = useState(activeTabId);
    const [animationTarget, setAnimationTarget] = useState({
        width: 0,
        x: 0,
    });
    const flattenedChildren = children.flat();
    const tabIds = React.Children.map(flattenedChildren, (child) => child.props.id);
    const currentTabIndex = tabIds.indexOf(currentTabId);
    const currentFocusIndex = tabIds.indexOf(currentFocusId);
    const [isUsingKeyboard, setIsUsingKeyboard] = useState(false);
    useEffect(() => {
        const globalOnClickHandler = () => setIsUsingKeyboard(false);
        window.addEventListener('click', globalOnClickHandler);
        return () => {
            window.removeEventListener('click', globalOnClickHandler);
        };
    }, []);
    useEffect(() => {
        setCurrentTab(activeTabId);
    }, [activeTabId]);
    const moveFocusFirst = () => {
        setCurrentFocus(tabIds[0]);
        scrollToTabId(tabIds[0]);
    };
    const moveFocusLast = () => {
        setCurrentFocus(tabIds[tabIds.length - 1]);
        scrollToTabId(tabIds[tabIds.length - 1]);
    };
    const moveFocusLeft = () => {
        const nextIndex = cycleIndexLeft(currentFocusIndex, tabIds.length);
        setCurrentFocus(tabIds[nextIndex]);
        scrollToTabId(tabIds[nextIndex]);
    };
    const moveFocusRight = () => {
        const nextIndex = cycleIndexRight(currentFocusIndex, tabIds.length);
        setCurrentFocus(tabIds[nextIndex]);
        scrollToTabId(tabIds[nextIndex]);
    };
    const handleChange = (event, id) => {
        if (event.currentTarget.getAttribute('aria-disabled') === 'true') {
            return;
        }
        setCurrentTab(id);
        setCurrentFocus(id);
        if (onChange) {
            onChange(event, id);
        }
    };
    function handleKeyPress(event) {
        switch (event.key) {
            case 'Enter':
            case ' ': {
                event.preventDefault();
                setIsUsingKeyboard(true);
                if (!flattenedChildren[currentFocusIndex].props.disabled) {
                    handleChange(event, currentFocusId);
                }
                break;
            }
            case 'End': {
                event.preventDefault();
                setIsUsingKeyboard(true);
                moveFocusLast();
                break;
            }
            case 'Home': {
                event.preventDefault();
                setIsUsingKeyboard(true);
                moveFocusFirst();
                break;
            }
            case 'Left': // IE/Edge specific value
            case 'ArrowLeft': {
                event.preventDefault();
                setIsUsingKeyboard(true);
                moveFocusLeft();
                break;
            }
            case 'Right': // IE/Edge specific value
            case 'ArrowRight': {
                event.preventDefault();
                setIsUsingKeyboard(true);
                moveFocusRight();
                break;
            }
            default:
                break;
        }
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const shouldUpdateAnimationTarget = (ref) => animationTarget.width !== (ref === null || ref === void 0 ? void 0 : ref.offsetWidth) ||
        animationTarget.x !== ref.offsetLeft;
    return (React.createElement("div", Object.assign({ className: `tabs ${className}` }, props),
        React.createElement(TabBar, { role: "tablist", "aria-activedescendant": `tab-${currentFocusId}`, "aria-label": description, onKeyDown: (event) => handleKeyPress(event), tabIndex: 0, isUsingKeyboard: isUsingKeyboard },
            React.createElement(ButtonsContainer, null, React.Children.map(flattenedChildren, ({ props: { id, label, trackid, disabled } }) => {
                const isSelected = currentTabId === id;
                return (React.createElement(Button, { role: "tab", "aria-selected": isSelected, "aria-controls": id, onClick: (e) => handleChange(e, id), id: `tab-${id}`, tabIndex: -1, type: "button", "data-trackid": trackid, "aria-disabled": disabled },
                    React.createElement(ButtonText, { active: isSelected, ref: (ref) => {
                            if (ref && id === currentTabId) {
                                if (shouldUpdateAnimationTarget(ref)) {
                                    setAnimationTarget({
                                        width: ref.offsetWidth,
                                        x: ref.offsetLeft,
                                    });
                                }
                            }
                        } }, label)));
            })),
            React.createElement("div", null,
                React.createElement(ButtonUnderline, Object.assign({ "data-chromatic": "ignore" }, animationTarget)))),
        flattenedChildren[currentTabIndex]));
}
export { Tab, TabBar };
export default Tabs;
